/**
 * Incident Report Template Form
 * Production incident documentation
 */
import { useState, useEffect, useRef } from "react";
import { downloadTemplatePDF } from "../utils/pdfGenerator";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { ArrowLeft, Download, Sparkles, RotateCcw, Plus, Trash2 , Save, Clock , FileText} from "lucide-react";
import { RAISection, FollowupsSection, API, useDCTMFile, FileUp, FileDown } from "./shared";
import { useAuth, useSettings } from "../App";

const IncidentReportForm = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { activeSettings } = useSettings();
  const [loading, setLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const [saving, setSaving] = useState(false);
  const [autoSave, setAutoSave] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [savedTemplateId, setSavedTemplateId] = useState(null);
  const autoSaveIntervalRef = useRef(null);
  const dctmFileInputRef = useRef(null);
  
  // Refs to track latest values for auto-save (avoids stale closures)
  const formDataRef = useRef(null);
  const savedTemplateIdRef = useRef(null);
  const editingSavedId = searchParams.get('saved');

  const getEmptyForm = () => ({
    incident_id: '', title: '', date: new Date().toISOString().split('T')[0], reported_by: '',
    severity: 'Medium', priority: 'Medium', status: 'Open', category: '',
    description: '', impact: '', affected_systems: [''], affected_users: '',
    detection_time: '', response_time: '', resolution_time: '', incident_commander: '',
    responders: [''], timeline: [{ timestamp: '', action: '', actor: '', notes: '' }],
    root_cause: '', resolution: '', workaround: '',
    rai_assignments: [], followups: []
  });

  const getSampleData = () => ({
    incident_id: 'INC-2025-001',
    title: 'Database Connection Pool Exhaustion',
    date: new Date().toISOString().split('T')[0],
    reported_by: 'Monitoring System',
    severity: 'High',
    priority: 'High',
    status: 'Resolved',
    category: 'Performance',
    description: 'Production database connection pool was exhausted causing API timeouts and service degradation. Users experienced slow page loads and intermittent 503 errors.',
    impact: 'Approximately 2,500 users affected. API response times increased from 200ms to 5000ms+. 15% of requests resulted in 503 errors.',
    affected_systems: ['API Gateway', 'User Service', 'Order Service', 'PostgreSQL Primary'],
    affected_users: '~2,500 active users during incident window',
    detection_time: '2025-01-15 14:30 UTC',
    response_time: '2025-01-15 14:35 UTC',
    resolution_time: '2025-01-15 15:45 UTC',
    incident_commander: 'John Smith (SRE Lead)',
    responders: ['John Smith (SRE)', 'Jane Doe (Backend Lead)', 'Mike Wilson (DBA)'],
    timeline: [
      { timestamp: '14:30', action: 'Alert triggered - High DB connection count', actor: 'PagerDuty', notes: 'Threshold: 90% pool utilization' },
      { timestamp: '14:35', action: 'Incident acknowledged, IC assigned', actor: 'John Smith', notes: 'Started investigation' },
      { timestamp: '14:42', action: 'Identified slow queries causing connection hold', actor: 'Mike Wilson', notes: 'Query taking 30s+ instead of 100ms' },
      { timestamp: '14:50', action: 'Implemented connection timeout reduction', actor: 'Jane Doe', notes: 'Reduced from 30s to 5s' },
      { timestamp: '15:05', action: 'Killed long-running queries', actor: 'Mike Wilson', notes: '12 queries terminated' },
      { timestamp: '15:20', action: 'Connection pool stabilized', actor: 'System', notes: 'Utilization dropped to 40%' },
      { timestamp: '15:45', action: 'Incident resolved, monitoring normal', actor: 'John Smith', notes: 'All services green' }
    ],
    root_cause: 'A recent code deployment introduced a query without proper indexing. Under high load, this query caused connection starvation as connections waited for slow queries to complete.',
    resolution: '1. Terminated stuck queries\n2. Reduced connection timeout\n3. Added missing index\n4. Increased connection pool size temporarily',
    workaround: 'Users could refresh the page to get a new connection. Support team communicated workaround via status page.',
    rai_assignments: [
      { task: 'Add missing database index', responsible: 'Backend Team', accountable: 'Tech Lead', informed: 'DBA, SRE', due_date: '2025-01-16', status: 'Completed' },
      { task: 'Add query performance tests to CI', responsible: 'QA Team', accountable: 'QA Lead', informed: 'Dev Team', due_date: '2025-01-20', status: 'In Progress' }
    ],
    followups: [
      { item: 'Schedule post-mortem meeting', owner: 'SRE Lead', due_date: '2025-01-17', priority: 'High', status: 'Completed' },
      { item: 'Update runbook with resolution steps', owner: 'SRE Team', due_date: '2025-01-22', priority: 'Medium', status: 'Pending' }
    ]
  });

  const [formData, setFormData] = useState(getEmptyForm());
  
  // Keep refs in sync with state
  useEffect(() => {
    formDataRef.current = formData;
  }, [formData]);
  
  useEffect(() => {
    savedTemplateIdRef.current = savedTemplateId;
  }, [savedTemplateId]);

  const loadSavedTemplate = async (id) => {
    try {
      const headers = user?.email ? { 'X-User-Email': user.email } : {};
      const response = await axios.get(`${API}/saved-templates/${id}`, { headers });
      if (response.data && response.data.data) {
        setFormData(response.data.data);
        setSavedTemplateId(id);
      }
    } catch (error) {
      console.error("Error loading saved template:", error);
    }
  };

  // Internal save function that accepts values directly (for use with refs)
  const saveDraftInternal = async (dataToSave, existingSavedId, isAutoSave = false) => {
    if (!isAutoSave) setSaving(true);
    try {
      const draftName = dataToSave.project_name || dataToSave.title || dataToSave.company_name || "Incident Report Draft";
      const payload = {
        name: draftName,
        template_type: "Incident Report",
        template_id: "incident-report",
        format: "DOCX",
        data: dataToSave
      };
      const headers = user?.email ? { 'X-User-Email': user.email } : {};
      
      if (existingSavedId) {
        const response = await axios.put(`${API}/saved-templates/${existingSavedId}`, payload, { headers });
        // Update version if returned from server
        if (response.data?.version) {
          setFormData(prev => ({ ...prev, version: response.data.version }));
        }
      } else {
        const response = await axios.post(`${API}/saved-templates`, payload, { headers });
        if (response.data?.id) {
          setSavedTemplateId(response.data.id);
        }
      }
      setLastSaved(new Date());
      if (!isAutoSave) setSaving(false);
      return true;
    } catch (error) {
      console.error("Error saving draft:", error);
      if (!isAutoSave) setSaving(false);
      return false;
    }
  };

  const handleSaveDraft = async (isAutoSave = false) => {
    const success = await saveDraftInternal(formData, savedTemplateId, isAutoSave);
    if (!isAutoSave) {
      if (success) {
        alert("Draft saved successfully!");
      } else {
        alert("Error saving draft.");
      }
    }
  };

  // Load saved template if editing
  useEffect(() => {
    if (editingSavedId) {
      loadSavedTemplate(editingSavedId);
    }
  }, [editingSavedId]);

  // Auto-save functionality - uses refs to avoid stale closure issues
  useEffect(() => {
    if (autoSave && user?.email) {
      autoSaveIntervalRef.current = setInterval(() => {
        const currentFormData = formDataRef.current;
        const currentSavedId = savedTemplateIdRef.current;
        if (currentFormData) {
          saveDraftInternal(currentFormData, currentSavedId, true);
        }
      }, 30000);
    } else {
      if (autoSaveIntervalRef.current) {
        clearInterval(autoSaveIntervalRef.current);
        autoSaveIntervalRef.current = null;
      }
    }
    return () => {
      if (autoSaveIntervalRef.current) {
        clearInterval(autoSaveIntervalRef.current);
        autoSaveIntervalRef.current = null;
      }
    };
  }, [autoSave, user?.email]);

  // DCTM Import/Export handlers
  const handleExportDCTM = () => {
    const { exportToDCTM } = require("./shared");
    exportToDCTM(formData, "incident-report", "Incident Report", user?.email || '');
  };

  const handleImportDCTM = async (file) => {
    const { importFromDCTM, validateDCTMTemplate } = require("./shared");
    const result = await importFromDCTM(file);
    
    if (!result.success) {
      alert(result.error);
      return;
    }
    
    // Validate template compatibility
    const validation = validateDCTMTemplate("incident-report", result.meta);
    if (validation.warning) {
      const proceed = window.confirm(`Warning: ${validation.warning}

Do you want to continue importing?`);
      if (!proceed) return;
    }
    
    // Increment version for imported data
    const currentVersion = parseFloat(result.data.version) || 1.0;
    const newVersion = (currentVersion + 0.1).toFixed(1);
    
    // Set form data with incremented version
    setFormData({ ...result.data, version: newVersion });
    setSavedTemplateId(null);
    
    alert(`Document imported successfully!
Original version: ${result.meta.documentVersion}
New version: ${newVersion}`);
  };


  const handleDownload = async () => {
    // Save draft before downloading
    await saveDraftInternal(formData, savedTemplateId, false);
    setLoading(true);
    try {
      const response = await axios.post(`${API}/generate/incident-report`, { ...formData, created_by_email: user?.email || "" }, { responseType: "blob" });
      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = blobUrl;
      link.setAttribute("download", `Incident_Report_${formData.incident_id || 'Document'}.docx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) { console.error("Error:", error); alert("Error generating document."); }
    setLoading(false);
  };

  const handleDownloadPDF = async () => {
    // Save draft before downloading
    await saveDraftInternal(formData, savedTemplateId, false);
    setPdfLoading(true);
    try {
      await downloadTemplatePDF({
        title: formData.title || formData.project_name || formData.meeting_title || formData.company_name || "Incident Report",
        formData,
        settings: activeSettings || {},
        templateType: "Incident Report",
        templateId: "generate",
        createdBy: user?.email,
        selectedSetting: activeSettings?.id,
        allSettings: null
      });
    } catch (error) {
      console.error("Error generating PDF:", error);
      alert(error.message || "Error generating PDF. Please try again.");
    }
    setPdfLoading(false);
  };

  const addTimelineEntry = () => {
    setFormData({ ...formData, timeline: [...formData.timeline, { timestamp: '', action: '', actor: '', notes: '' }] });
  };

  const updateTimeline = (index, field, value) => {
    const timeline = [...formData.timeline];
    timeline[index] = { ...timeline[index], [field]: value };
    setFormData({ ...formData, timeline });
  };

  const removeTimeline = (index) => {
    setFormData({ ...formData, timeline: formData.timeline.filter((_, i) => i !== index) });
  };

  const severities = ['Critical', 'High', 'Medium', 'Low'];
  const priorities = ['Critical', 'High', 'Medium', 'Low'];
  const statuses = ['Open', 'Investigating', 'Identified', 'Monitoring', 'Resolved', 'Closed'];
  const categories = ['System', 'Security', 'Performance', 'Data', 'Network', 'Third-Party', 'Other'];

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm border-b sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate("/")} data-testid="back-btn"><ArrowLeft className="h-5 w-5" /></Button>
              <div><h1 className="text-xl font-bold">Incident Report</h1><p className="text-sm text-gray-500">Production incident documentation</p></div>
            </div>
            <div className="flex gap-2 items-center flex-wrap justify-end">
              <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 border border-blue-200 rounded-md">
                <Clock className="h-4 w-4 text-blue-600" />
                <span className="text-sm font-medium text-blue-700">Auto-save</span>
                <Switch checked={autoSave} onCheckedChange={setAutoSave} data-testid="auto-save-toggle" />
              </div>
              <Button variant="outline" size="sm" onClick={() => setFormData(getSampleData())} data-testid="fill-sample-btn"><Sparkles className="h-4 w-4 mr-2" /> Sample</Button>
              <Button variant="outline" size="sm" onClick={() => { setFormData(getEmptyForm()); setSavedTemplateId(null); }} data-testid="reset-btn"><RotateCcw className="h-4 w-4 mr-2" /> Reset</Button>
            </div>
          </div>
          {lastSaved && (
            <div className="text-xs text-gray-500 mt-2 text-right flex items-center justify-end gap-3">
              {formData.version && <span className="bg-gray-100 px-2 py-0.5 rounded">v{formData.version}</span>}
              <span>Last saved: {lastSaved.toLocaleTimeString()}</span>
            </div>
          )}
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-8">
        <Card>
          <CardHeader><CardTitle>Incident Details</CardTitle></CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div><Label>Incident ID *</Label><Input value={formData.incident_id} onChange={(e) => setFormData({...formData, incident_id: e.target.value})} placeholder="INC-2025-001" /></div>
              <div className="col-span-2"><Label>Title *</Label><Input value={formData.title} onChange={(e) => setFormData({...formData, title: e.target.value})} placeholder="Brief incident title" /></div>
              <div><Label>Date</Label><Input type="date" value={formData.date} onChange={(e) => setFormData({...formData, date: e.target.value})} /></div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <div><Label>Severity</Label>
                <Select value={formData.severity} onValueChange={(v) => setFormData({...formData, severity: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{severities.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Priority</Label>
                <Select value={formData.priority} onValueChange={(v) => setFormData({...formData, priority: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{priorities.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Status</Label>
                <Select value={formData.status} onValueChange={(v) => setFormData({...formData, status: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{statuses.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Category</Label>
                <Select value={formData.category} onValueChange={(v) => setFormData({...formData, category: v})}>
                  <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Reported By</Label><Input value={formData.reported_by} onChange={(e) => setFormData({...formData, reported_by: e.target.value})} placeholder="Name/System" /></div>
            </div>

            <Separator />

            <div><Label>Description</Label><Textarea value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} placeholder="Detailed description of the incident..." rows={3} /></div>
            <div><Label>Impact</Label><Textarea value={formData.impact} onChange={(e) => setFormData({...formData, impact: e.target.value})} placeholder="Business and user impact..." rows={2} /></div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Affected Systems</Label>
                {formData.affected_systems.map((sys, idx) => (
                  <div key={idx} className="flex gap-2 mt-1">
                    <Input value={sys} onChange={(e) => { const s = [...formData.affected_systems]; s[idx] = e.target.value; setFormData({...formData, affected_systems: s}); }} placeholder="System name" />
                    <Button variant="ghost" size="icon" onClick={() => setFormData({...formData, affected_systems: formData.affected_systems.filter((_, i) => i !== idx)})}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                  </div>
                ))}
                <Button variant="outline" size="sm" onClick={() => setFormData({...formData, affected_systems: [...formData.affected_systems, '']})} className="mt-2"><Plus className="h-4 w-4 mr-1" /> Add</Button>
              </div>
              <div><Label>Affected Users</Label><Input value={formData.affected_users} onChange={(e) => setFormData({...formData, affected_users: e.target.value})} placeholder="Number/description of affected users" /></div>
            </div>

            <Separator />

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div><Label>Detection Time</Label><Input value={formData.detection_time} onChange={(e) => setFormData({...formData, detection_time: e.target.value})} placeholder="2025-01-15 14:30 UTC" /></div>
              <div><Label>Response Time</Label><Input value={formData.response_time} onChange={(e) => setFormData({...formData, response_time: e.target.value})} placeholder="2025-01-15 14:35 UTC" /></div>
              <div><Label>Resolution Time</Label><Input value={formData.resolution_time} onChange={(e) => setFormData({...formData, resolution_time: e.target.value})} placeholder="2025-01-15 15:45 UTC" /></div>
              <div><Label>Incident Commander</Label><Input value={formData.incident_commander} onChange={(e) => setFormData({...formData, incident_commander: e.target.value})} placeholder="Name (Role)" /></div>
            </div>

            <Separator />

            <div>
              <Label className="font-semibold">Timeline</Label>
              <div className="space-y-2 mt-2">
                {formData.timeline.map((entry, idx) => (
                  <div key={idx} className="grid grid-cols-1 md:grid-cols-5 gap-2 bg-gray-50 p-2 rounded">
                    <Input value={entry.timestamp} onChange={(e) => updateTimeline(idx, 'timestamp', e.target.value)} placeholder="Time" className="text-sm" />
                    <div className="md:col-span-2"><Input value={entry.action} onChange={(e) => updateTimeline(idx, 'action', e.target.value)} placeholder="Action taken" className="text-sm" /></div>
                    <Input value={entry.actor} onChange={(e) => updateTimeline(idx, 'actor', e.target.value)} placeholder="Who" className="text-sm" />
                    <div className="flex gap-1">
                      <Input value={entry.notes} onChange={(e) => updateTimeline(idx, 'notes', e.target.value)} placeholder="Notes" className="text-sm" />
                      <Button variant="ghost" size="icon" onClick={() => removeTimeline(idx)}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                    </div>
                  </div>
                ))}
              </div>
              <Button variant="outline" size="sm" onClick={addTimelineEntry} className="mt-2"><Plus className="h-4 w-4 mr-1" /> Add Entry</Button>
            </div>

            <Separator />

            <div><Label>Root Cause</Label><Textarea value={formData.root_cause} onChange={(e) => setFormData({...formData, root_cause: e.target.value})} placeholder="What caused the incident..." rows={3} /></div>
            <div><Label>Resolution</Label><Textarea value={formData.resolution} onChange={(e) => setFormData({...formData, resolution: e.target.value})} placeholder="How the incident was resolved..." rows={3} /></div>
            <div><Label>Workaround</Label><Textarea value={formData.workaround} onChange={(e) => setFormData({...formData, workaround: e.target.value})} placeholder="Temporary workaround if any..." rows={2} /></div>

            <Separator />
            <RAISection formData={formData} setFormData={setFormData} />
            <FollowupsSection formData={formData} setFormData={setFormData} />

            <div className="flex flex-col gap-3">
              <div className="flex gap-3">
                <Button onClick={() => handleSaveDraft(false)} disabled={saving || loading || pdfLoading} variant="outline" className="flex-1" size="lg" data-testid="save-draft-btn">
                <Save className="h-5 w-5 mr-2" /> {saving ? "Saving..." : (savedTemplateId ? "Update" : "Draft")}
              </Button>
              <Button onClick={handleDownload} disabled={loading || pdfLoading} className="flex-1" size="lg" data-testid="download-btn">
              <Download className="h-5 w-5 mr-2" /> {loading ? "Generating..." : "Download DOCX"}
            </Button>
              <Button onClick={handleDownloadPDF} disabled={loading || pdfLoading} variant="outline" className="flex-1" size="lg" data-testid="download-pdf-btn">
                  <FileText className="h-5 w-5 mr-2" /> {pdfLoading ? "Generating..." : "PDF"}
              </Button>
              <input
                type="file"
                ref={dctmFileInputRef}
                accept=".dctm"
                onChange={(e) => {
                  if (e.target.files?.[0]) {
                    handleImportDCTM(e.target.files[0]);
                    e.target.value = '';
                  }
                }}
                className="hidden"
                data-testid="dctm-file-input"
              />
              <Button onClick={() => dctmFileInputRef.current?.click()} disabled={loading || pdfLoading} variant="outline" className="flex-1" size="lg" data-testid="import-dctm-btn">
                <FileUp className="h-5 w-5 mr-2" /> Import
              </Button>
              <Button onClick={handleExportDCTM} disabled={loading || pdfLoading} variant="outline" className="flex-1" size="lg" data-testid="export-dctm-btn">
                <FileDown className="h-5 w-5 mr-2" /> Export
              </Button>
              </div>
              {(loading || pdfLoading) && (
                <div className="flex items-center justify-center gap-2 py-2 px-4 bg-blue-50 border border-blue-200 rounded-md">
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-600 border-t-transparent"></div>
                  <span className="text-sm text-blue-700">
                    {loading ? "Generating DOCX document..." : "Generating PDF document..."}
                  </span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default IncidentReportForm;
